'use client';

import { TCatalog } from '@/actions/catalogs/types';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import { getTranslation } from '@/lib/utils';
import parser, {
  DOMNode,
  domToReact,
  HTMLReactParserOptions,
} from 'html-react-parser';
import Image from 'next/image';
import Link from 'next/link';
import React, { HTMLAttributes, useRef, useState } from 'react';
import Slider from 'react-slick';
import './styles.css';
import styles from './styles.module.css';

interface IGalleryProps extends HTMLAttributes<HTMLDivElement> {
  catalogExperiences: TCatalog[];
}

export const Gallery: React.FC<IGalleryProps> = ({ catalogExperiences }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { experiences: dict } = useTranslation();
  const lang = useLocale();

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    afterChange: (currentPage: number) => {
      setCurrentPage(currentPage);
    },
  };

  const parseOptions: HTMLReactParserOptions = {
    replace: (node, index) => {
      if (node.type === 'tag' && node.name === 'a' && node.children) {
        return (
          <span key={index}>
            <strong>
              {domToReact(node.children as DOMNode[], parseOptions)}
            </strong>
          </span>
        );
      }
    },
  };

  const sliderRef = useRef<Slider>(null);

  const goToSlide = (index: number): void => {
    sliderRef.current?.slickGoTo(index);
  };

  const bannerNavOptions = catalogExperiences.map((exp) => {
    const catalog = exp.translate.find((item) => {
      if (item.locale === lang) {
        return item.title;
      }
    });

    return catalog?.title;
  });

  const galleryOptions = catalogExperiences.map((item, index) => {
    const experiencesUrls = {
      en: 'experiences',
      it: 'esperienze',
      de: 'erfahrungen',
      fr: 'experiences',
    };
    const translation = getTranslation(item.translate, lang);
    const href =
      lang === 'en'
        ? `experiences/${translation.slug}`
        : `${experiencesUrls[lang]}/${translation.slug}`;

    const filteredImage =
      item.image!.mediaFilters?.find(({ name }) => name === '1920w_webp') ??
      item.image!.mediaFilters[item.image!.mediaFilters.length - 1];

    return (
      <div
        key={index}
        className={`${styles.banner} flex relative h-[360px] desktop:h-[650px] w-full`}>
        <Image
          src={filteredImage.url}
          alt={translation.title}
          fill
          className="absolute h-full brightness-75"
          quality={85}
          sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
          style={{ objectFit: 'cover' }}
        />
        <div className="absolute flex flex-col justify-center w-full max-w-[500px] desktop:w-[30%] h-full px-[30px] desktop:px-[60px] py-[30px] desktop:py-[60px] gap-8 text-wrap text-white">
          <h2 className="text-4xl mb-2 desktop:mb-8 font-noe">
            {translation.title}
          </h2>
          {parser(translation.shortDescription || '', parseOptions)}
          <Link
            tabIndex={-1}
            href={href}
            className="text-sm tracking-[2px] font-averta-semibold">
            <button
              tabIndex={-1}
              className="w-full h-12 mobileL:max-h-16 mobileL:min-h-16 py-[8px] laptop:py-[15px] tracking-[2px] bg-primary uppercase">
              {dict.gallery.button}
            </button>
          </Link>
        </div>
      </div>
    );
  });

  return (
    <div className="mb-20 mt-24">
      <h2 className="text-title font-title font-bold w-full text-center">
        {dict.gallery.title}
      </h2>
      <div className="text-center text-gray-850 text-lg mt-5 mb-[60px]">
        {parser(dict.gallery.subtitle)}
      </div>

      <div className="flex justify-center w-full p-2 tablet:p-6 desktop:p-0">
        <div className="relative flex flex-col justify-center w-[95%] md:w-full">
          <Slider ref={sliderRef} {...settings} className="tour-carousel">
            {galleryOptions}
          </Slider>
          <div className="absolute hidden desktop:flex flex-wrap justify-center bottom-0 space-x-8 w-full px-1 pb-6">
            {bannerNavOptions.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goToSlide(index)}
                className={`${
                  currentPage !== index && 'brightness-75'
                } hover:brightness-100`}>
                <span
                  className={`${
                    currentPage === index && 'underline'
                  } text-white text-xl`}>
                  {item}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
